// Version detection utility using package.json version
export const getCurrentVersion = () => {
    return process.env.REACT_APP_VERSION || '0.0.0';
};

/**
 * Derive public web domain from API hostname (release notes live on the app domain, not the API host).
 * Handles api.prod2.example.com, prod2api.example.com, test2api.example.com, dev2api.example.com, and localhost.
 * @param {string} rawHostname - URL hostname only
 * @returns {string}
 */
const hostnameToReleaseNotesDomain = (rawHostname) => {
    let hostname = rawHostname.toLowerCase();
    hostname = hostname.replace(/^api\./, '');
    hostname = hostname.replace(/^([a-z]+\d*)api\./, '$1.');
    return hostname;
};

const ENV_TO_RELEASE_NOTES_DOMAIN = {
    prod: 'perinhealth.com',
    prod2: 'prod2.perinhealth.com',
    dev2: 'dev2.perinhealth.com',
    dev: 'dev.perinhealth.com',
    test2: 'test2.perinhealth.com',
    test: 'test.perinhealth.com',
    sandbox: 'sandbox.perinhealth.com',
};

const getReleaseNotesDomainFromEnv = () =>
    ENV_TO_RELEASE_NOTES_DOMAIN[process.env.REACT_APP_ENVIRONMENT_STATUS] ?? 'dev2.perinhealth.com';

/**
 * Get release notes URL based on environment
 * @param {string} envURL - Backend API base URL (e.g. from axiosConfig.envURL)
 * @returns {string} URL to the release notes PDF
 */
export const getReleaseNotesUrl = (envURL) => {
    let domain = 'dev2.perinhealth.com';
    if (envURL) {
        try {
            const url = new URL(envURL);
            const hostname = hostnameToReleaseNotesDomain(url.hostname);
            // If localhost, default to dev2
            if (hostname === 'localhost' || hostname === '127.0.0.1') {
                domain = 'dev2.perinhealth.com';
            } else {
                domain = hostname;
            }
        } catch (e) {
            domain = getReleaseNotesDomainFromEnv();
        }
    } else {
        domain = getReleaseNotesDomainFromEnv();
    }
    return `https://${domain}/release-notes.pdf`;
};

/**
 * Check for app updates. Uses backend /latestVersion when baseURL is provided (works in dev and with V1/V2 backend).
 * Falls back to static /version.json (e.g. CodePipeline) when baseURL is not set or backend fails.
 * @param {string} [baseURL] - Backend API base URL (e.g. from axiosConfig.envURL). When set, fetches /latestVersion from backend.
 * @returns {Promise<{hasUpdate: boolean, currentVersion: string, latestVersion: string, updateInfo: null, buildTime: null}>}
 */
export const checkForUpdates = async (baseURL) => {
    const currentVersion = getCurrentVersion();
    const fallback = {
        hasUpdate: false,
        currentVersion,
        latestVersion: currentVersion,
        updateInfo: null,
        buildTime: null
    };

    // Prefer backend /latestVersion when we have a base URL (V1 and V2 both expose GET /latestVersion)
    if (baseURL) {
        try {
            const url = `${baseURL.replace(/\/$/, '')}/latestVersion`;
            const response = await fetch(url, {
                cache: 'no-store',
                headers: { 'Content-Type': 'application/json' }
            });
            if (!response.ok) return fallback;
            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                return fallback;
            }
            const body = await response.json();
            // V1/V2 shape: { code: 201, message, data: { appVersion, ... } }
            const latestVersion = body?.data?.appVersion || currentVersion;
            return {
                hasUpdate: latestVersion !== currentVersion,
                currentVersion,
                latestVersion,
                updateInfo: body?.data?.appNotes || null,
                buildTime: body?.data?.createdDate || null
            };
        } catch (error) {
            console.warn('Version check (backend) failed:', error.message || error);
            return fallback;
        }
    }

    // Fallback: static /version.json (e.g. production CodePipeline)
    try {
        const response = await fetch('/version.json', {
            cache: 'no-store',
            headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
        });
        if (!response.ok) return fallback;
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
            // Dev server often returns HTML for unknown routes; avoid parsing and avoid throwing
            return fallback;
        }
        const versionData = await response.json();
        const latestVersion = versionData.version || currentVersion;
        return {
            hasUpdate: latestVersion !== currentVersion,
            currentVersion,
            latestVersion,
            updateInfo: versionData.updateInfo || null,
            buildTime: versionData.buildTime || null
        };
    } catch (error) {
        console.warn('Version check failed:', error.message || error);
        return fallback;
    }
};
